import React, {Component} from 'react';
import {withTranslation} from 'react-i18next';
import {MANDATORY_COLUMNS} from "./columns";
import {resolveColumnLabel} from "./sheetIssues";
import {FaIcon} from "../../FaIcon";

/**
 * Una fila del mapeo `columna de Dashbo → header del CSV`.
 *
 * COMPLETAMENTE CONTROLADA por el padre: no guarda el header elegido en state propio. Lo hacía, y
 * era una UI que podía mentir — el padre monta estas filas con `key={columnMapping.column}`, que NO
 * cambia en un re-import, así que no hay remount que sincronice nada: si el padre reseteaba el
 * mapeo (importar otro archivo en edición), el `<select>` seguía mostrando el header viejo mientras
 * el submit mandaba el del padre. El valor que se ve y el que se guarda tienen que salir del mismo
 * lugar.
 */
class ColumnMappingConfigurationComponent extends Component {
    constructor(props) {
        super(props);

        this.handleHeaderChange = this.handleHeaderChange.bind(this);
    }

    handleHeaderChange(event) {
        const newHeader = event.target.value !== "" ? event.target.value : null;

        this.props.onValueChange(this.props.order, this.props.column, newHeader);
    }

    isMandatory() {
        return MANDATORY_COLUMNS.includes(this.props.column);
    }

    isMapped() {
        const headerValue = this.props.header;
        return headerValue !== null && headerValue !== undefined && headerValue !== "";
    }

    renderHeaderSelect() {
        if (!this.props.headersOptions) {
            return <input type="text" className="form-control" readOnly/>;
        }

        return (
            <select className="form-control"
                    value={this.props.header || ""}
                    onChange={this.handleHeaderChange}
                    required={this.isMandatory()}>
                <option value="" key="no-mapped">—</option>
                {this.props.headersOptions.map((option) => (
                    <option value={option} key={option}>{option}</option>
                ))}
            </select>
        );
    }

    handleApplySuggestion(suggestion) {
        if (this.props.onApplySuggestion) this.props.onApplySuggestion(suggestion);
    }

    handleApplyHeader(header) {
        if (this.props.onApplyHeaderSuggestion) this.props.onApplyHeaderSuggestion(this.props.column, header);
    }

    /**
     * La propuesta de re-mapeo de ESTA fila, con el nombre viejo a la vista.
     *
     * Decir de dónde sale la propuesta —«antes apuntaba a "Total Cost"»— es lo que la vuelve
     * verificable: sin eso el usuario tiene que confiar en una sugerencia que no sabe de dónde
     * salió, y el costo de aceptar una equivocada lo paga en los números del cliente.
     */
    renderHeaderSuggestion() {
        const {t, suggestedHeader, previousHeader} = this.props;
        if (!suggestedHeader) return null;

        return (
            <div className="sheet-import-mapping-remap">
                <span className="sheet-import-mapping-remap-text">
                    {t(previousHeader
                        ? 'sheet_account.remap.row_suggestion_with_previous'
                        : 'sheet_account.remap.row_suggestion',
                        {header: suggestedHeader, previous: previousHeader, interpolation: {escapeValue: false}})}
                </span>
                <button type="button"
                        className="sheet-import-mapping-issue-fix"
                        onClick={() => this.handleApplyHeader(suggestedHeader)}>
                    {t('sheet_account.remap.row_apply')}
                </button>
            </div>
        );
    }

    /**
     * Cuando dos columnas del archivo podrían ser ésta, se PREGUNTA en vez de elegir a cara o
     * cruz — la misma regla que ya usa el formato de fecha ambiguo. Callarse era peor que las
     * dos: dejaba al usuario sin la sugerencia y sin enterarse de que había una duda.
     */
    renderHeaderCandidates() {
        const {t, headerCandidates, header} = this.props;
        if (!headerCandidates || headerCandidates.length === 0) return null;

        // Una vez elegido, la pregunta cambia: ya no es "¿cuál usás?" sino cuál quedó. Sin esto,
        // las tres opciones seguían idénticas después de clickear una y nada decía cuál era —
        // justo lo contrario de la propuesta simple, que desaparece al aceptarla.
        const alreadyChosen = headerCandidates.includes(header);

        return (
            <div className="sheet-import-mapping-remap">
                <span className="sheet-import-mapping-remap-text">
                    {t(alreadyChosen ? 'sheet_account.remap.which_one_chosen' : 'sheet_account.remap.which_one')}
                </span>
                {headerCandidates.map((candidate) => {
                    const isChosen = candidate === header;
                    return (
                        <button type="button"
                                key={candidate}
                                className={"sheet-import-mapping-issue-fix" + (isChosen ? " -chosen" : "")}
                                aria-pressed={isChosen}
                                onClick={() => this.handleApplyHeader(candidate)}>
                            {candidate}
                        </button>
                    );
                })}
            </div>
        );
    }

    /** El dato que trae hoy la columna elegida. Elegir de una lista de nombres es adivinar. */
    renderPreview() {
        const {t, preview} = this.props;
        if (!preview || !this.isMapped()) return null;

        return (
            <span className="sheet-import-mapping-preview">
                {t('sheet_account.remap.preview', {value: preview, interpolation: {escapeValue: false}})}
            </span>
        );
    }

    /**
     * El error se ancla a la FILA del mapeo que lo causó: un problema en la columna Fecha se pinta
     * acá, no en un alert suelto que no dice dónde mirar.
     */
    renderIssues() {
        const issues = this.props.issues || [];
        if (issues.length === 0 && !this.props.moreIssuesText) return null;

        return (
            <div className="sheet-import-mapping-issues" role="alert">
                <ul className="sheet-import-mapping-issues-list">
                    {issues.map((issue) => (
                        <li key={issue.key}>
                            <span className="sheet-import-mapping-issue-text">{issue.text}</span>
                            {issue.suggestion && (
                                <button type="button"
                                        className="sheet-import-mapping-issue-fix"
                                        onClick={() => this.handleApplySuggestion(issue.suggestion)}>
                                    {issue.suggestionLabel}
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
                {this.props.moreIssuesText && (
                    <span className="sheet-import-mapping-issues-more">{this.props.moreIssuesText}</span>
                )}
            </div>
        );
    }

    render() {
        const label = resolveColumnLabel(this.props.column, this.props.t);
        const hasIssues = (this.props.issues || []).length > 0 || !!this.props.moreIssuesText;

        const classes = [
            "sheet-import-mapping-row",
            this.isMandatory() ? "-required" : "",
            this.isMapped() ? "-mapped" : "",
            this.props.headerMissing ? "-broken" : "",
            hasIssues ? "-has-issues" : ""
        ].filter(Boolean).join(" ");

        return (
            <div className={classes}>
                <span className="sheet-import-mapping-pill">{label}</span>
                <FaIcon iconName="arrow-right" className="sheet-import-mapping-arrow"/>
                {this.renderHeaderSelect()}
                {this.props.headerMissing && (
                    <span className="sheet-import-mapping-broken-hint">
                        <FaIcon iconName="triangle-exclamation"/>
                        {this.props.t('sheet_account.diagnostic.header_no_longer_in_csv')}
                    </span>
                )}
                {this.renderPreview()}
                {this.renderHeaderSuggestion()}
                {this.renderHeaderCandidates()}
                {this.renderIssues()}
            </div>
        );
    }
}

export const ColumnMappingConfiguration = withTranslation()(ColumnMappingConfigurationComponent);
